import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Check, Package } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { Order } from '../context/AppContext';
import { ordersApi } from '../api/orders';

export default function CheckoutPage() {
  usePageTitle("Paiement — Lodiss");

  const { lang, cart, clearCart, addOrder, setPage, currentUser } = useApp();
  const T = t[lang];

  const [form, setForm] = useState({
    name: currentUser?.full_name || '',
    company: currentUser?.company || '',
    phone: currentUser?.phone || '',
    email: currentUser?.email || '',
    address: '',
    city: '',
    payment: 'delivery'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);
  const [orderRef, setOrderRef] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const total = cart.reduce((sum, i) => sum + i.product.price * i.qty, 0);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = lang === 'fr' ? 'Requis' : 'Required';
    if (!form.phone.trim()) e.phone = lang === 'fr' ? 'Requis' : 'Required';
    if (!form.email.trim() || !form.email.includes('@')) e.email = lang === 'fr' ? 'Email invalide' : 'Invalid email';
    if (!form.address.trim()) e.address = lang === 'fr' ? 'Requis' : 'Required';
    if (!form.city.trim()) e.city = lang === 'fr' ? 'Requis' : 'Required';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const ref = 'ORD-' + Date.now().toString(36).toUpperCase();
    setOrderRef(ref);

    const orderItems = cart.map(item => ({
      product_id: item.product.id,
      quantity: item.qty,
      unit_price: item.product.price,
      total: item.product.price * item.qty,
    }));

    const apiOrder = {
      items: orderItems,
      form_data: form,
    };

    try {
      setSubmitting(true);
      await ordersApi.createOrder(apiOrder);

      const order: Order = {
        id: ref,
        type: 'direct',
        items: [...cart],
        date: new Date().toISOString(),
        status: lang === 'fr' ? 'Confirmée' : 'Confirmed',
        form,
      };
      addOrder(order);
      clearCart();
      setConfirmed(true);
    } catch (err) {
      console.error('Failed to create order:', err);
      setErrors({ submit: lang === 'fr' ? 'Erreur lors de la commande' : 'Order failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const field = (key: keyof typeof form, label: string, type = 'text') => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 transition-colors ${errors[key]
            ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
            : 'border-gray-200 focus:border-lodiss-red focus:ring-lodiss-red/20'
          }`}
      />
      {errors[key] && <p className="text-xs text-red-500 mt-1">{errors[key]}</p>}
    </div>
  );

  if (confirmed) {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check size={28} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-950 mb-2">{T.checkout.success}</h2>
            <p className="text-sm text-gray-500 mb-4">{T.checkout.successMsg}</p>
            <div className="bg-gray-50 rounded-lg p-3 mb-6">
              <p className="text-xs text-gray-500">{T.checkout.orderRef}</p>
              <p className="text-sm font-bold text-gray-900 font-mono mt-1">{orderRef}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setPage('orders')}
                className="w-full py-2.5 text-sm font-semibold text-white bg-lodiss-red hover:bg-lodiss-dark rounded-lg transition-colors"
              >
                {T.checkout.track}
              </button>
              <button
                onClick={() => setPage('home')}
                className="w-full py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {lang === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-gray-950 mb-6">{T.checkout.title}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <form onSubmit={handleSubmit} className="lg:col-span-3 space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wide">
                {lang === 'fr' ? 'Coordonnées' : 'Contact details'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {field('name', T.checkout.name)}
                {field('company', T.checkout.company)}
                {field('phone', T.checkout.phone, 'tel')}
                {field('email', T.checkout.email, 'email')}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wide">
                {lang === 'fr' ? 'Livraison' : 'Delivery'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {field('address', T.checkout.address)}
                {field('city', T.checkout.city)}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wide">
                {T.checkout.payment}
              </h3>
              <div className="space-y-2">
                {[
                  { value: 'delivery', label: T.checkout.payDelivery },
                  { value: 'transfer', label: T.checkout.payTransfer },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="payment"
                      value={opt.value}
                      checked={form.payment === opt.value}
                      onChange={() => setForm(f => ({ ...f, payment: opt.value }))}
                      className="text-lodiss-red focus:ring-lodiss-red"
                    />
                    <span className="text-sm text-gray-700 font-medium">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (lang === 'fr' ? 'Envoi en cours...' : 'Placing order...') : T.checkout.submit}
            </button>
          </form>

          {/* Order summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-100 p-5 sticky top-36">
              <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wide flex items-center gap-2">
                <Package size={14} className="text-lodiss-red" />
                {lang === 'fr' ? 'Récapitulatif' : 'Summary'}
              </h3>
              <ul className="space-y-3 mb-4 pb-4 border-b border-gray-100">
                {cart.map(item => (
                  <li key={item.product.id} className="flex items-center gap-2">
                    <img src={item.product.images[0]} alt="" className="w-9 h-9 rounded-lg object-cover border border-gray-100 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">
                        {lang === 'fr' ? item.product.name : item.product.nameEn}
                      </p>
                      <p className="text-[11px] text-gray-400">x{item.qty}</p>
                    </div>
                    <span className="text-xs font-semibold text-gray-800 flex-shrink-0">
                      {(Number(item.product.price) * item.qty).toFixed(3)} TND
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{T.cart.total}</span>
                <span className="text-lg font-bold text-gray-950">
                  {formatPrice(total)} <span className="text-xs font-normal text-gray-400">{T.common.ht}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
